import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstructionSetsList from './InstructionSetsList';
import DatabaseConnectionDetails from './DatabaseConnectionDetails';
import SystemSettings from './SystemSettings';
import Tasks from './Tasks';
import CredentialsView from './CredentialsView';
import TableColumnConfig from '../pages/TableColumnConfig';

interface SettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onConnectionChange?: () => void;
}

const SettingsPopup: React.FC<SettingsPopupProps> = ({ isOpen, onClose, onConnectionChange }) => {
  const navigate = useNavigate();
  const [showInstructionSets, setShowInstructionSets] = useState(false);
  const [showConnectionDetails, setShowConnectionDetails] = useState(false);
  const [showSystemSettings, setShowSystemSettings] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [showTableColumnConfig, setShowTableColumnConfig] = useState(false);

  if (!isOpen) return null;

  const handleInstructionSetClick = () => {
    setShowInstructionSets(true);
  };

  const handleDatabaseConnectionClick = () => {
    setShowConnectionDetails(true);
  };

  const handleSystemSettingsClick = () => {
    setShowSystemSettings(true);
  };

  const handleTasksClick = () => {
    setShowTasks(true);
  };

  const handleCredentialsClick = () => {
    setShowCredentials(true);
  };

  const handleTableColumnConfigClick = () => {
    setShowTableColumnConfig(true);
  };

  const handleBackToSettings = () => {
    setShowInstructionSets(false);
    setShowConnectionDetails(false);
    setShowSystemSettings(false);
    setShowTasks(false);
    setShowCredentials(false);
    setShowTableColumnConfig(false);
  };

  // Show instruction sets list if requested
  if (showInstructionSets) {
    return (
      <InstructionSetsList
        isOpen={true}
        onClose={onClose}
        onBack={handleBackToSettings}
      />
    );
  }

  // Show database connection details if requested
  if (showConnectionDetails) {
    return (
      <DatabaseConnectionDetails
        isOpen={true}
        onClose={onClose}
        onBack={handleBackToSettings}
        onConnectionChange={onConnectionChange}
      />
    );
  }

  // Show system settings if requested
  if (showSystemSettings) {
    return (
      <SystemSettings
        isOpen={true}
        onClose={onClose}
        onBack={handleBackToSettings}
      />
    );
  }

  // Show tasks if requested
  if (showTasks) {
    return (
      <Tasks
        isOpen={true}
        onClose={onClose}
        onBack={handleBackToSettings}
      />
    );
  }

  // Show credentials if requested
  if (showCredentials) {
    return (
      <CredentialsView
        isOpen={true}
        onClose={onClose}
        onBack={handleBackToSettings}
      />
    );
  }

  // Show table column config if requested
  if (showTableColumnConfig) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-auto">
        <div className="w-full h-full overflow-auto">
          <div className="min-h-screen relative">
            <button
              onClick={handleBackToSettings}
              className="fixed top-4 left-4 z-[60] inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-gray-600 to-gray-700 border border-transparent rounded-lg hover:from-gray-700 hover:to-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
            >
              <span className="material-symbols-outlined mr-2" style={{ fontSize: '18px' }}>
                arrow_back
              </span>
              Back to Settings
            </button>
            <TableColumnConfig />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 w-96 max-w-90vw shadow-xl border border-slate-200 dark:border-slate-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Settings</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
              close
            </span>
          </button>
        </div>

        <div className="space-y-3">

          <button
            onClick={handleDatabaseConnectionClick}
            className="w-full flex items-center space-x-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-green-100 dark:bg-green-900 group-hover:bg-green-200 dark:group-hover:bg-green-800 transition-colors">
              <span className="material-symbols-outlined text-green-600 dark:text-green-400" style={{ fontSize: '20px' }}>
                storage
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Connections</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Manage database settings and schema</p>
            </div>
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>
              chevron_right
            </span>
          </button>

          {/* <button
            onClick={handleCredentialsClick}
            className="w-full flex items-center space-x-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-800 transition-colors">
              <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400" style={{ fontSize: '20px' }}>
                key
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">My Credentials</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">View Tenant ID and API Key</p>
            </div>
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>
              chevron_right
            </span>
          </button> */}

          <button
            onClick={handleTableColumnConfigClick}
            className="w-full flex items-center space-x-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-purple-100 dark:bg-purple-900 group-hover:bg-purple-200 dark:group-hover:bg-purple-800 transition-colors">
              <span className="material-symbols-outlined text-purple-600 dark:text-purple-400" style={{ fontSize: '20px' }}>
                table_chart
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Tables & Columns</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Configure table and column visibility</p>
            </div>
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>
              chevron_right
            </span>
          </button>

          <button
            onClick={handleInstructionSetClick}
            className="w-full flex items-center space-x-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-blue-100 dark:bg-blue-900 group-hover:bg-blue-200 dark:group-hover:bg-blue-800 transition-colors">
              <span className="material-symbols-outlined text-blue-600 dark:text-blue-400" style={{ fontSize: '20px' }}>
                smart_toy
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Instruction Set</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Configure AI model instructions</p>
            </div>
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>
              chevron_right
            </span>
          </button>

          <button
            onClick={handleSystemSettingsClick}
            className="w-full flex items-center space-x-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 dark:bg-red-900 group-hover:bg-red-200 dark:group-hover:bg-red-800 transition-colors">
              <span className="material-symbols-outlined text-red-600 dark:text-red-400" style={{ fontSize: '20px' }}>
                settings_applications
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">System Settings</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Neo4j embeddings and graph management</p>
            </div>
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>
              chevron_right
            </span>
          </button>

          <button
            onClick={handleTasksClick}
            className="w-full flex items-center space-x-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800 transition-colors">
              <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400" style={{ fontSize: '20px' }}>
                task
              </span>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Tasks</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">View and manage system tasks</p>
            </div>
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500" style={{ fontSize: '16px' }}>
              chevron_right
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPopup;